import React, { useState, useEffect, useMemo } from "react";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  SelectDropdown,
  SelectOption,
} from "components/main/utils/SelectDropdown";
import { TextInput } from "components/common/TextInput";
import { DatePickerInput } from "components/common/DatePickerInput";
import { CodeBlock } from "components/common/CodeBlock";
import { useTokensStore } from "components/main/stores/tokensStore";
import {
  getMinCustomDate,
  EXPIRATION_OPTIONS,
  CUSTOM_EXPIRATION_VALUE,
  calculateExpirationMinutesFromDate,
} from "./utils";
import { DialogField } from "@/components/main/utils/DialogField";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { Dialog } from "@/components/main/utils/Dialog";
import { showNotificationGlobal } from "stores/useNotificationStore";

interface TokenDialogProps {
  onGenerate: () => Promise<void>;
  onDone: () => void;
}

export const TokenDialog: React.FC<TokenDialogProps> = ({
  onGenerate,
  onDone,
}) => {
  const {
    dialogOpen,
    newToken,
    generatedToken,
    isGeneratingToken,
    updateNewToken,
    closeDialog,
    resetDialog,
  } = useTokensStore();
  // Minting binds to the ACTIVE org (S8) — shown as a fixed fact, the
  // same way the OAuth consent screen shows it.
  const activeOrg = useOrgContextStore((s) =>
    s.orgs.find((o) => o.org_id === s.activeOrgId),
  );
  const activeOrgLabel = activeOrg
    ? activeOrg.is_personal
      ? "Personal"
      : activeOrg.name
    : "—";
  const [customDate, setCustomDate] = useState<Date | null>(null);

  const isCustomExpiration = useMemo(
    () =>
      newToken.expiration_time === CUSTOM_EXPIRATION_VALUE ||
      !EXPIRATION_OPTIONS.some((opt) => opt.value === newToken.expiration_time),
    [newToken.expiration_time],
  );

  useEffect(() => {
    if (isCustomExpiration && customDate) {
      const dateString = customDate.toISOString().split("T")[0];
      updateNewToken({
        expiration_time: calculateExpirationMinutesFromDate(dateString),
      });
    }
  }, [customDate, isCustomExpiration, updateNewToken]);

  const handleExpirationChange = (value: string | number) => {
    const numValue = typeof value === "string" ? parseInt(value, 10) : value;
    const isCustom = numValue === CUSTOM_EXPIRATION_VALUE;
    updateNewToken({
      expiration_time: isCustom ? CUSTOM_EXPIRATION_VALUE : numValue,
    });
    if (isCustom) setCustomDate(null);
  };

  useEffect(() => {
    if (!dialogOpen) {
      setCustomDate(null);
      resetDialog();
    }
  }, [dialogOpen, resetDialog]);

  return (
    <Dialog
      open={dialogOpen}
      title="New personal access token"
      onClose={closeDialog}
    >
      {generatedToken ? (
        <>
          <div className="flex items-center item-height">
            <p className="text-fg-regular">
              Make sure to copy your personal access token now.{" "}
              <strong> You won't be able to see it again! </strong>
            </p>
          </div>

          <DialogField>
            <CodeBlock
              code={generatedToken}
              ariaLabel="Generated personal access token"
              onCopy={() =>
                showNotificationGlobal("Token copied to clipboard", "success")
              }
            />
          </DialogField>

          <DialogField>
            <p className="text-fg-muted-regular-small">
              Use it with the Python SDK (
              <code className="font-mono">MYCORR_API_TOKEN</code>) or the REST
              API. To use MyCorr in an AI client, connect via MCP instead — no
              token needed.
            </p>
          </DialogField>

          <DialogActions
            actions={[
              {
                label: "Done",
                variant: "primary",
                onClick: onDone,
              },
            ]}
          />
        </>
      ) : (
        <>
          <DialogField label="Description">
            <TextInput
              value={newToken.description}
              onChange={(value) => updateNewToken({ description: value })}
              placeholder="Enter token description"
              disabled={isGeneratingToken}
              className="input-custom-sm w-full"
            />
          </DialogField>

          <DialogField label="Organization">
            <span className="text-fg-regular">
              {activeOrgLabel}
              <span className="text-fg-muted">
                {" "}
                · the token is confined to this organization's data
              </span>
            </span>
          </DialogField>
          <DialogField label="Scope">
            <SelectDropdown
              value={newToken.scope}
              onChange={(value) =>
                updateNewToken({ scope: value as "read" | "write" })
              }
              options={
                [
                  { label: "Read-only", value: "read" },
                  { label: "Read & write", value: "write" },
                ] as SelectOption[]
              }
              size="md"
              menuWidthClass="w-full"
            />
            <p className="text-fg-muted-regular-small mt-1.5">
              {newToken.scope === "write"
                ? "Can read and modify your data (e.g. create tables, edit rows)."
                : "Can read your data only."}
            </p>
          </DialogField>

          <DialogField label="Expiration">
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <SelectDropdown
                  value={
                    isCustomExpiration
                      ? CUSTOM_EXPIRATION_VALUE
                      : newToken.expiration_time
                  }
                  onChange={handleExpirationChange}
                  options={
                    EXPIRATION_OPTIONS.map((o) => ({
                      label: o.label,
                      value: o.value,
                    })) as SelectOption[]
                  }
                  size="md"
                  menuWidthClass="w-full"
                />
              </div>
              {isCustomExpiration && (
                <div className="flex-1 min-w-0">
                  <DatePickerInput
                    value={customDate}
                    onChange={setCustomDate}
                    minDate={new Date(getMinCustomDate())}
                    disabled={isGeneratingToken}
                  />
                </div>
              )}
            </div>
          </DialogField>

          <DialogActions
            actions={[
              // Dismiss is the header's Close (X) / Esc — no redundant Cancel.
              {
                label: "Generate",
                variant: "primary",
                onClick: onGenerate,
                disabled: isGeneratingToken || !newToken.description.trim(),
                loading: isGeneratingToken,
              },
            ]}
          />
        </>
      )}
    </Dialog>
  );
};
